import { useEffect, useRef, useState } from "react";

const FREEZE_THRESHOLD_MS = 5_000;
const CHECK_INTERVAL_MS = 500;

/**
 * Detects genuine main-thread freezes using foreground-only time.
 * Uses performance.now() deltas measured only while the page is visible,
 * so background throttling and screen sleep never trigger a false positive.
 * Requires two consecutive detections before showing the banner.
 */
export function FreezeBanner() {
  const [frozen, setFrozen] = useState(false);
  const freezeCount = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let lastCheck = performance.now();
    let active = true;

    const tick = () => {
      if (!active) return;
      // Only measure when the page is actually visible to the user.
      // If hidden, reset the baseline and do not count elapsed time.
      if (document.visibilityState !== "visible") {
        lastCheck = performance.now();
        return;
      }
      const now = performance.now();
      const gap = now - lastCheck;
      lastCheck = now;
      if (gap > FREEZE_THRESHOLD_MS) {
        freezeCount.current += 1;
        // Require two consecutive detections to rule out a single
        // scheduling anomaly or GC pause.
        if (freezeCount.current >= 2) {
          setFrozen(true);
        }
      } else {
        // Reset count on any normal tick — freeze must be sustained.
        freezeCount.current = 0;
      }
    };

    const id = window.setInterval(tick, CHECK_INTERVAL_MS);

    const onVisibilityChange = () => {
      // Reset baseline whenever tab becomes visible so the first
      // tick after resume never counts background time.
      lastCheck = performance.now();
      freezeCount.current = 0;
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onVisibilityChange);
    window.addEventListener("pageshow", onVisibilityChange);

    return () => {
      active = false;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onVisibilityChange);
      window.removeEventListener("pageshow", onVisibilityChange);
    };
  }, []);

  if (!frozen) return null;

  return (
    <button
      type="button"
      onClick={() => window.location.reload()}
      className="fixed inset-x-0 z-50 mx-auto max-w-md text-center text-sm"
      style={{
        bottom: "calc(72px + env(safe-area-inset-bottom))",
        backgroundColor: "var(--caileve-soft)",
        color: "var(--caileve-ink)",
        border: "1px solid var(--caileve-lilac)",
        borderRadius: 12,
        padding: "var(--caileve-space-2) var(--caileve-space-5)",
        margin: "0 16px",
        boxShadow: "0 6px 24px var(--caileve-ink-08)",
      }}
    >
      Something went wrong.{" "}
      <span style={{ color: "var(--caileve-lilac)", textDecoration: "underline" }}>
        Tap to refresh.
      </span>
    </button>
  );
}